import { useEffect } from 'react';
import { Button } from '../ui/button';
import { capture } from '../../lib/analytics';
import { stripeRedirectErrorCopy } from '../../lib/stripeRedirect';
import { useStripeRedirect } from '../../hooks/useStripeRedirect';

/**
 * The plan surface for a team that pays through Stripe: one button to
 * Stripe's own customer portal (card, invoices, cancel). Clerk's
 * PricingTable must never stand here: Clerk has no record of a Stripe
 * subscription, so it would offer to sell the owner the plan they already
 * hold, through a second biller.
 */
export function ManageStripeSubscription({ inline = false, disabled = false }: { inline?: boolean; disabled?: boolean } = {}) {
  const { open, pendingKey, errorCode } = useStripeRedirect();
  const isOpening = pendingKey === 'portal';

  return (
    <div className={inline ? "billing-plan-management mt-4" : "card-sm"}>
      {!inline && <h2 className="section-label mb-3">Subscription</h2>}
      <Button
        type="button"
        variant="outline"
        size="sm"
        isLoading={isOpening}
        disabled={disabled}
        aria-busy={isOpening}
        onClick={() => {
          capture('billing_manage_subscription_clicked', { provider: 'stripe' });
          void open({ kind: 'portal' });
        }}
      >
        Manage subscription
      </Button>
      {errorCode && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {stripeRedirectErrorCopy(errorCode)}
        </p>
      )}
    </div>
  );
}

const CHECKOUT_PARAM = 'checkout';

/** Read and REMOVE this component's own query param, leaving any others. */
function takeCheckoutParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(CHECKOUT_PARAM);
  if (value === null) return null;
  params.delete(CHECKOUT_PARAM);
  const rest = params.toString();
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
  return value;
}

/**
 * Stripe returns the owner to /billing?checkout=success BEFORE its webhook has
 * necessarily landed, so for a few seconds the page would still read "trial".
 * This hands the return to the page's activation flow (useSubscriptionActivation:
 * pending card, poll, success message), the same flow Clerk's drawer uses.
 *
 * The param is STRIPPED before the hand-off. That is what keeps a reload
 * from re-running it, and what makes React StrictMode's second mount a no-op
 * without a ref latch: the second mount finds nothing to take.
 */
export function CheckoutReturnRefresher({ onSuccessReturn }: { onSuccessReturn: () => void }) {
  useEffect(() => {
    if (takeCheckoutParam() !== 'success') return;
    capture('stripe_checkout_returned', { outcome: 'success' });
    onSuccessReturn();
    // Mount-only by design: the param is consumed on first read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}
